import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Standard SEO meta-tag kit shared across institute public sites.
 *
 * Renders title (= institute name verbatim), description, keywords, canonical,
 * Open Graph, Twitter Card, theme-color, format-detection and JSON-LD
 * CollegeOrUniversity schema in a single Helmet block.
 *
 * Per-institute OG image / favicon assets are optional — pass them as props.
 * When omitted, the institute's `logo_url` is used as the og:image.
 */
const KEYWORDS_FULL =
  "Marathwada Institute of Technology, MIT CIDCO, MIT Aurangabad, MIT Chhatrapati Sambhajinagar, GS Mandal, Engineering College Aurangabad, Engineering College Sambhajinagar, Best Engineering College in Aurangabad, Best Engineering College in Maharashtra, Engineering Admission Aurangabad, Computer Science Engineering, CSE Engineering, Computer Engineering, Information Technology, IT Engineering, Artificial Intelligence, Artificial Intelligence and Data Science, AI and DS, Data Science Engineering, Electronics Engineering, Electronics and Telecommunication Engineering, ENTC Engineering, Electrical Engineering, Mechanical Engineering, Civil Engineering, Automobile Engineering, Production Engineering, Chemical Engineering, Diploma Engineering, Polytechnic College, Polytechnic Aurangabad, Diploma in Computer Engineering, Diploma in Information Technology, Diploma in Mechanical Engineering, Diploma in Civil Engineering, Diploma in Electrical Engineering, Diploma in Electronics Engineering, Bachelor of Engineering, BE Degree, BTech Degree, Engineering Courses Maharashtra, Professional Courses, Technical Education, Engineering Programs, Undergraduate Engineering, Postgraduate Engineering, MBA, Master of Business Administration, Management Studies, Postgraduate Programs, Dr Babasaheb Ambedkar Marathwada University, DBATU, Engineering Education, Higher Education Maharashtra, Career Oriented Education, Industry Oriented Education, Placement Assistance, Campus Placement, Engineering Internship, Research and Innovation, Quest for Excellence, GS Mandal Educational Institutions, College in CIDCO, College in Aurangabad, College in Sambhajinagar, Engineering Institute Maharashtra, Technical Institute Maharashtra";

export default function InstituteSeo({
  slug,
  inst,
  themeColor = "#0D2B52",
  ogImage,
  faviconHref,
  appleTouchHref,
  description,
}) {
  const name = (inst && inst.name) || "GS Mandal Institute";
  const city = (inst && inst.city) || "";
  const canonical = `https://gsmandal.org/${slug}`;
  const desc =
    description ||
    `${name}${city ? `, ${city}` : ""} — a GS Mandal institute. Quality education with a Quest for Excellence.`;
  const ogImg = ogImage || (inst && inst.logo_url) || "";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollegeOrUniversity",
    name,
    url: canonical,
    ...(ogImg ? { image: ogImg } : {}),
    ...((inst && inst.logo_url) ? { logo: inst.logo_url } : {}),
    description: desc,
    address: {
      "@type": "PostalAddress",
      addressLocality: city || "Chhatrapati Sambhajinagar",
      addressRegion: "Maharashtra",
      addressCountry: "IN",
    },
    parentOrganization: {
      "@type": "Organization",
      name: "GS Mandal",
      url: "https://gsmandal.org",
    },
  };

  return (
    <Helmet>
      {/* Primary */}
      <title>{name}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={KEYWORDS_FULL} />
      <meta name="author" content="GS Mandal" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={name} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="GS Mandal" />
      {ogImg && <meta property="og:image" content={ogImg} />}
      {ogImg && <meta property="og:image:secure_url" content={ogImg} />}
      {ogImg && <meta property="og:image:alt" content={`${name} — GS Mandal`} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={name} />
      <meta name="twitter:description" content={desc} />
      {ogImg && <meta name="twitter:image" content={ogImg} />}
      {ogImg && <meta name="twitter:image:alt" content={`${name} — GS Mandal`} />}

      {/* Mobile / Browser */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content={themeColor} />
      <meta name="format-detection" content="telephone=no" />

      {/* Favicon */}
      {faviconHref && <link rel="icon" href={faviconHref} />}
      {appleTouchHref && <link rel="apple-touch-icon" href={appleTouchHref} />}

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}
